import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  getTeamCompanyUsers,
  getAllUsers,
  getPerformanceUsers,
  getRelievedEmployees,
  getCompanyByUser,
  getPerformanceByUser,
  getRelievedEmployeesLegacy,
  getCustomerAdmins,
  createPerformanceRecord,
  uploadPerformanceFile,
  createRelievedEmployeeRecord,
  getCompanyEmployeesForChartApi,
} from "../../services/axios/AuthApis/TeamCollabApis";


import {
  UsersIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ChartBarIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import TabNavigation from "../ui/TabNavigation";
import PageLoader from "../ui/PageLoader";
import ApiLoader from "../ui/ApiLoader";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import UploadFileModal from "../ui/UploadFileModal";
import TeamCollabrationDashboard from "./tabs/DashBoard";
import MembersTabComponent from "./tabs/MembersTab";
import CommunicationTabComponent from "./tabs/CommunicationTab";
import BGVTabComponent from "./tabs/BGVTab";
import TrainingTabComponent from "./tabs/TrainingTab";
import PolicyTabComponent from "./tabs/PolicyTab";
import PerformanceTabComponent from "./tabs/PerformanceTab";
import RelievedEmployeeTabComponent from "./tabs/RelievedEmployeeTab";

const TeamCollaboration = () => {
  const { user, token } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const mountedRef = useRef(false);

  // Original states
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [customerMasterId, setCustomerMasterId] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [policyRecords, setPolicyRecords] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [hrmsSource, setHrmsSource] = useState("none");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileDescription, setFileDescription] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Team Members");
  const [selectedFile, setSelectedFile] = useState(null);

  const getTabLoaderLabel = () => {
    if (selectedTab === "communication") return "Loading communication...";
    if (selectedTab === "members") return "Loading team members...";
    return "Loading dashboard...";
  };

  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    onConfirm: () => { },
  });

  const [performanceUploadFile, setPerformanceUploadFile] = useState(false);

  // New states for Team Collaboration features
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [relievedEmployees, setRelievedEmployees] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [, setAllUsers] = useState([]);
  const setBgvForm = () => {};


  // Modal states for new features
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showRelievedModal, setShowRelievedModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalMode, setModalMode] = useState("create"); // "create" or "edit"
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [trainingTabPagination, setTrainingTabPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [policyTabPagination, setPolicyTabPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [relievedPagination, setRelievedPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
  });

  const [performanceForm, setPerformanceForm] = useState({
    firstname: "",
    department: "",
    date: "",
    reporting_to: "",
    status: "",
  });

  const [relievedForm, setRelievedForm] = useState({
    employee_id: "",
    employee_name: "",
    department: "",
    role: "",
    doj: "",
    lwd: "",
    reason_for_leaving: "",
    manager: "",
    clearance_status: "pending",
    assets_returned: false,
    access_revoked: false,
    exit_interview_completed: false,
    final_settlement: false,
    rehire_eligible: true,
    remarks: "",
  });

  const [performancePagination, setPerformancePagination] = useState({
    currentPage: 1,
    pageSize: 10,
    total: 0,
  });

  const [companyEmployeesForChart, setCompanyEmployeesForChart] = useState([]);
  
  const hasTeamCollabAccess = user?.employee_details?.module_access_to?.["team-collaboration"] === true;

  const navigate = useNavigate();

  const loadPerformanceUsers = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true); // show loader
      let customerId = user?.customer_master_id;
      if (user?.role === "security_analyst" && selectedCompany) {
        customerId = selectedCompany.id;
      }
      const res = await getPerformanceUsers({
        customer_master_id: customerId,
        page,
        page_size: pageSize,
      });

      setPerformanceRecords(res.results || []);

      setPerformancePagination({
        totalPages: res.total_pages,
        totalCount: res.count,
        currentPage: res.current_page,
        pageSize: res.page_size,
      });
    } catch (err) {
      console.error("Error fetching performance users:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load company employees for org chart with HRMS support
  const loadCompanyEmployeesForChart = useCallback(async () => {
    try {
      setChartLoading(true);
      if (!customerMasterId && !selectedCompany) return;
      const customerId = selectedCompany?.id || customerMasterId;

      const data = await getCompanyEmployeesForChartApi(customerId);

      // Transform response to expected format
      const chartData = (data.results || []).map((emp) => ({
        employee_id: emp.employee_id,
        id: emp.employee_id,
        firstname: emp.firstname || "",
        lastname: emp.lastname || "",
        name: `${emp.firstname || ""} ${emp.lastname || ""}`.trim(),
        email: emp.email || "",
        phone: emp.phone || "",
        department: emp.department || "",
        designation: emp.designation || "",
        reporting_to: emp.reporting_to || "",
        reporting_to_id: emp.reporting_to_id || "",
        company_name: emp.company_name || "",
        external_id: emp.external_id || "",
        profile_pic: emp.profile_pic || "",
        source: emp.source || "database",
      }));

      setCompanyEmployeesForChart(chartData);
      setHrmsSource(data.hrms_source || "none");
      console.log(
        `Loaded ${chartData.length} employees for org chart from ${data.hrms_source}`,
      );
    } catch (error) {
      console.error("Error fetching company employees for chart:", error);
    } finally {
      setChartLoading(false);
    }
  }, [customerMasterId, selectedCompany]);


  useEffect(() => {
    if (selectedTab === "dashboard") {
      loadCompanyEmployeesForChart();
    } else if (selectedTab === "performance") {
      loadPerformanceUsers();
    }
  }, [selectedTab, loadCompanyEmployeesForChart]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (selectedTab === "relieved") {
      loadRelievedEmployees();
    }
  }, [selectedTab]);

  const loadRelievedEmployees = async (
    page = 1,
    pageSize = 20,
    search = "",
  ) => {
    try {
      setLoading(true);
      let userId = user?.id;
      if (user?.role === "security_analyst" && selectedCompany) {
        userId = selectedCompany.user_id;
      }
      const data = await getRelievedEmployees({
        userId: userId,
        page,
        pageSize,
        search,
        token,
      });

      setRelievedEmployees(data.results || []);

      setRelievedPagination({
        totalPages: data.total_pages || 1,
        totalCount: data.count || 0,
        currentPage: data.current_page || page,
        pageSize: data.page_size || pageSize,
      });
    } catch (error) {
      console.error("Relieved employee fetch error:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchAllUsers = async () => {
    try {
      setLoading(true);

      const data = await getAllUsers();

      setAllUsers(data.results || []);
    } catch (err) {
      console.error("Error fetching user master:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllUsers();
  }, []);
  // const getUserFirstName = (userId) => {
  //   const match = allUsers.find((u) => u.id === userId);
  //   return match?.firstname || match?.firstName || "â€”";
  // };

  // Load data based on selected tab
  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [selectedTab, token]);

  useEffect(() => {
    if (user?.role !== "customer_admin" && !(user?.role === "security_analyst" && selectedCompany) && !(user?.role === "end_user" && hasTeamCollabAccess) ) {
      setCompanyName("");
      return;
    }
    const fetchCompanyName = async () => {
      try {
        setLoading(true);
        let customerId = user?.id;
        if (user?.role === "security_analyst" && selectedCompany) {
          customerId = selectedCompany.user_id;
        }else if(user?.role === "end_user" && hasTeamCollabAccess){
          customerId = user?.customer_user_id;
        }
        const data = await getCompanyByUser(customerId);
        if (!data) {
          console.warn("No company found for this user");
          setCompanyName("");
          setCustomerMasterId("");
          return;
        }
        setCompanyName(data.company_name || "");
        setCustomerMasterId(data.customer_master_id || "");

      } catch (err) {
        console.error("Error fetching company name:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyName();
  }, [user]);
  const loadData = async () => {
    if (!token) return;
    if (!["bgv", "performance", "relieved", "training", "policy"].includes(selectedTab)) {
      return;
    }

    setLoading(true);
    try {
      let userId = user?.id;
      let customerId = customerMasterId;
      if (user?.role === "security_analyst" && selectedCompany) {
        userId = selectedCompany.user_id;
        customerId = selectedCompany.id;
      }
      switch (selectedTab) {
        // ================= PERFORMANCE =================
        case "performance":
          try {
            const data = await getPerformanceByUser(userId, token);
            console.log("Performance API response:", data);
            setPerformanceRecords(data.results || []);
          } catch (err) {
            console.error("Failed loading performance:", err);
          }
          break;

        // ================= RELIEVED =================
        case "relieved":
          try {
            const relievedData = await getRelievedEmployeesLegacy(
              customerId,
              token,
            );

            setRelievedEmployees(relievedData?.data?.records || []);
          } catch (err) {
            console.error("Failed loading relieved employees:", err);
          }
          break;

        // ================= TRAINING =================
        case "training":
          try {
            const data = await getCustomerAdmins(user?.id, token);
            const employees = data.team_members || [];

            const enriched = employees.map((emp) => ({
              employee_name: `${emp.firstname} ${emp.lastname}`,
              total_assigned: 0,
              completed_count: 0,
              completed_date: null,
              status: "pending",
            }));

            setTrainingRecords(enriched);
          } catch (err) {
            console.error("Error fetching training data:", err);
          }
          break;

        // ================= POLICY =================
        case "policy":
          try {
            const data = await getCustomerAdmins(user?.id, token);
            const employees = data.team_members || [];

            const enriched = employees.map((emp) => ({
              employee_name: `${emp.firstname} ${emp.lastname}`,
              total_assigned: 0,
              completed_date: null,
              status: "pending",
            }));

            setPolicyRecords(enriched);
          } catch (err) {
            console.error("Error fetching policy data:", err);
          }
          break;

      }
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Failed to load team collaboration data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePerformanceRecord = async () => {
    if (!token) return;

    try {
      await createPerformanceRecord({
        payload: performanceForm,
        token,
      });

      showSnackbar("Performance record created successfully", "success");
      setShowPerformanceModal(false);
      resetPerformanceForm();
      loadData();
    } catch (error) {
      console.error("Error creating performance record:", error);
      showSnackbar("Failed to create performance record", "error");
    }
  };

  const handlePerformUploadFile = async () => {
    if (!selectedFile) {
      showSnackbar("Please select a file", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("description", fileDescription);

    try {
      await uploadPerformanceFile({
        employeeId: selectedRecord.employee_id,
        formData,
        userId: user.id,
      });

      showSnackbar("File uploaded successfully!", "success");
      setSelectedFile(null);
      setFileDescription("");
    } catch (err) {
      console.error("Unexpected error:", err);
      showSnackbar("Unexpected error", "error");
    }
  };
  const handleCreateRelievedRecord = async () => {
    if (!token) return;

    try {
      await createRelievedEmployeeRecord({
        payload: relievedForm,
        token,
      });

      showSnackbar("Relieved employee record created successfully", "success");
      setShowRelievedModal(false);
      resetRelievedForm();
      loadData();
    } catch (error) {
      console.error("Error creating relieved employee record:", error);
      showSnackbar("Failed to create relieved employee record", "error");
    }
  };

  const resetPerformanceForm = () => {
    setPerformanceForm({
      firstname: "",
      department: "",
      date: "",
      reporting_to: "",
      status: "",
    });
  };

  const resetRelievedForm = () => {
    setRelievedForm({
      employee_id: "",
      employee_name: "",
      department: "",
      role: "",
      doj: "",
      lwd: "",
      reason_for_leaving: "",
      manager: "",
      clearance_status: "pending",
      assets_returned: false,
      access_revoked: false,
      exit_interview_completed: false,
      final_settlement: false,
      rehire_eligible: true,
      remarks: "",
    });
  };

  const trainingTotalPages = Math.max(
    Math.ceil(trainingRecords.length / trainingTabPagination.pageSize),
    1,
  );
  const policyTotalPages = Math.max(
    Math.ceil(policyRecords.length / policyTabPagination.pageSize),
    1,
  );

  const paginatedTrainingRecords = useMemo(() => {
    const start =
      (trainingTabPagination.currentPage - 1) * trainingTabPagination.pageSize;
    const end = start + trainingTabPagination.pageSize;
    return trainingRecords.slice(start, end);
  }, [
    trainingRecords,
    trainingTabPagination.currentPage,
    trainingTabPagination.pageSize,
  ]);

  const paginatedPolicyRecords = useMemo(() => {
    const start =
      (policyTabPagination.currentPage - 1) * policyTabPagination.pageSize;
    const end = start + policyTabPagination.pageSize;
    return policyRecords.slice(start, end);
  }, [
    policyRecords,
    policyTabPagination.currentPage,
    policyTabPagination.pageSize,
  ]);

  useEffect(() => {
    setTrainingTabPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [trainingRecords.length]);

  useEffect(() => {
    setPolicyTabPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [policyRecords.length]);

  const getPerformanceRatingColor = (rating) => {
    switch (rating) {
      case "excellent":
        return "bg-green-100 text-green-800";
      case "good":
        return "bg-blue-100 text-blue-800";
      case "satisfactory":
        return "bg-yellow-100 text-yellow-800";
      case "needs_improvement":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getClearanceStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const recentMessages = [
    {
      id: 1,
      sender: "Sarah Johnson",
      message: "Updated the compliance checklist for Q2 audit preparation.",
      timestamp: "2 hours ago",
      avatar: "SJ",
    },
    {
      id: 2,
      sender: "Mike Chen",
      message: "Completed vulnerability scan for all critical systems.",
      timestamp: "4 hours ago",
      avatar: "MC",
    },
  ];

  const handleSendMessage = () => {
    console.log("Sending message:", newMessage);
    setNewMessage("");
  };

  // const bgvTableData = filteredTeamMembers.map((member) => {
  //   const userObj = allUsers.find((u) => u.id === member.user_id);

  //   const bgvObj = bgvRecords.find((rec) => rec.employee_id === member.employee_id);

  //   const agencyId = bgvObj?.verified_by?.agency_id;

  //   return {
  //     employee_id: member.e,
  //     employee_name: userObj?.firstname || "â€”",
  //     department: member.department || "â€”",
  //     doj: member.date_of_joining || "â€”",

  //     // FIXED BGV STATUS
  //     bgv_status:
  //       bgvObj?.status === 1 ? "Active"
  //       : bgvObj?.status === 0 ? "Inactive"
  //       : "â€”",

  //     // FIXED AGENCY NAME LOOKUP
  //     bgv_agency: agencyMap[agencyId] || "â€”",
  //   };
  // });

  if (loading && selectedTab === "dashboard" && !dashboardData) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading Team Collaboration..." minH="min-h-screen" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-6 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Team Collaboration
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage team members, background verification, performance, and
            employee lifecycle
          </p>
        </div>

        {/* Vertical Tab Layout */}
        {(() => {
          const tabs = user?.role !== "admin" ? [
            { id: "dashboard",    name: "Dashboard",              icon: ChartBarIcon },
            { id: "members",      name: "Team Members",           icon: UsersIcon },
            { id: "bgv",          name: "Background Verification",icon: ShieldCheckIcon },
            { id: "training",     name: "Training",               icon: AcademicCapIcon },
            { id: "policy",       name: "Policy Acknowledgement", icon: ClipboardDocumentListIcon },
            { id: "performance",  name: "Performance Management", icon: ClipboardDocumentListIcon },
            { id: "relieved",     name: "Relieved Employee",      icon: UserMinusIcon },
          ] : [];

          const tabContent = (
            <AnimatePresence mode="wait">
              {selectedTab === "dashboard" && (
                <motion.div key="dashboard" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {chartLoading
                    ? <ApiLoader label="Loading Dashboard…" className="py-20" />
                    : <TeamCollabrationDashboard dashboardData={dashboardData} companyEmployeesForChart={companyEmployeesForChart} isOdooConfigured={!!lastSyncedAt} hrmsSource={hrmsSource} />
                  }
                </motion.div>
              )}
              {selectedTab === "members" && (
                <motion.div key="members" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  <MembersTabComponent />
                </motion.div>
              )}
              {selectedTab === "communication" && (
                <motion.div key="communication" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  <CommunicationTabComponent newMessage={newMessage} setNewMessage={setNewMessage} handleSendMessage={handleSendMessage} recentMessages={recentMessages} />
                </motion.div>
              )}
              {selectedTab === "bgv" && (
                <motion.div key="bgv" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {loading ? <ApiLoader label="Loading BGV…" className="py-20" /> : <BGVTabComponent dashboardData={dashboardData} customerMasterId={customerMasterId} />}
                </motion.div>
              )}
              {selectedTab === "training" && (
                <motion.div key="training" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {loading && trainingRecords.length === 0 ? <ApiLoader label="Loading Training…" className="py-20" /> : <TrainingTabComponent showSnackbar={showSnackbar} trainingRecords={trainingRecords} paginatedTrainingRecords={paginatedTrainingRecords} trainingTabPagination={trainingTabPagination} trainingTotalPages={trainingTotalPages} setTrainingTabPagination={setTrainingTabPagination} loading={loading} />}
                </motion.div>
              )}
              {selectedTab === "policy" && (
                <motion.div key="policy" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {loading && policyRecords.length === 0 ? <ApiLoader label="Loading Policies…" className="py-20" /> : <PolicyTabComponent showSnackbar={showSnackbar} policyRecords={policyRecords} paginatedPolicyRecords={paginatedPolicyRecords} policyTabPagination={policyTabPagination} policyTotalPages={policyTotalPages} setPolicyTabPagination={setPolicyTabPagination} loading={loading} />}
                </motion.div>
              )}
              {selectedTab === "performance" && (
                <motion.div key="performance" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {loading && performanceRecords.length === 0 ? <ApiLoader label="Loading Performance…" className="py-20" /> : <PerformanceTabComponent dashboardData={dashboardData} setModalMode={setModalMode} resetPerformanceForm={resetPerformanceForm} setShowPerformanceModal={setShowPerformanceModal} performanceRecords={performanceRecords} setSelectedRecord={setSelectedRecord} setBgvForm={setBgvForm} navigate={navigate} setPerformanceUploadFile={setPerformanceUploadFile} canUploadFiles={user?.role === "customer_admin" || user?.role === "security_analyst"} performancePagination={performancePagination} loadPerformanceUsers={loadPerformanceUsers} loading={loading} />}
                </motion.div>
              )}
              {selectedTab === "relieved" && (
                <motion.div key="relieved" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {loading && relievedEmployees.length === 0 ? <ApiLoader label="Loading Records…" className="py-20" /> : <RelievedEmployeeTabComponent dashboardData={dashboardData} setModalMode={setModalMode} resetRelievedForm={resetRelievedForm} setShowRelievedModal={setShowRelievedModal} relievedEmployees={relievedEmployees} getClearanceStatusColor={getClearanceStatusColor} setSelectedRecord={setSelectedRecord} setRelievedForm={setRelievedForm} relievedPagination={relievedPagination} loadRelievedEmployees={loadRelievedEmployees} loading={loading} />}
                </motion.div>
              )}
            </AnimatePresence>
          );

          return (
            <>
              {tabs.length > 0 && (
                <TabNavigation
                  tabs={tabs}
                  activeTab={selectedTab}
                  onTabChange={setSelectedTab}
                />
              )}
              {tabContent}
            </>
          );
        })()}

        {/* Member Details Modal */}
        <AnimatePresence>
          {showMemberDetails && selectedMember && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Member Details
                    </h3>
                    <button
                      onClick={() => setShowMemberDetails(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="flex items-center gap-4 mb-8 pb-8 border-b border-gray-200">
                    <div className="flex-shrink-0">
                      {selectedMember?.profile_pic ? (
                        <img
                          src={`data:image/png;base64,${selectedMember.profile_pic}`}
                          alt={`${selectedMember?.firstname} ${selectedMember?.lastname}`}
                          className="w-20 h-20 rounded-full object-cover ring-2 ring-gray-200"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center text-gray-600 font-bold text-lg">
                          {(
                            (selectedMember?.firstname?.charAt(0) ||
                              selectedMember?.firstName?.charAt(0) ||
                              "") +
                            (selectedMember?.lastname?.charAt(0) ||
                              selectedMember?.lastName?.charAt(0) ||
                              "")
                          ).toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-gray-900">
                        {(
                          (selectedMember?.firstname ||
                            selectedMember?.firstName ||
                            "") +
                          " " +
                          (selectedMember?.lastname ||
                            selectedMember?.lastName ||
                            "")
                        ).trim() || "â€”"}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedMember?.designation ||
                          selectedMember?.role ||
                          "Team Member"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedMember?.department || "â€”"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                      <h5 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                        Contact Information
                      </h5>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium mb-0.5">
                              Email
                            </p>
                            <p className="text-sm text-gray-700 break-all">
                              {selectedMember?.email || "â€”"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 border-t border-gray-200 pt-3">
                          <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium mb-0.5">
                              Phone
                            </p>
                            <p className="text-sm text-gray-700">
                              {selectedMember?.phone || "â€”"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                      <h5 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                        Professional Information
                      </h5>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 font-medium mb-0.5">
                              Department
                            </p>
                            <p className="text-sm text-gray-700 font-medium">
                              {selectedMember?.department || "â€”"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 border-t border-gray-200 pt-3">
                          <UserGroupIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 font-medium mb-0.5">
                              Reports To
                            </p>
                            <p className="text-sm text-gray-700 font-medium">
                              {selectedMember?.reporting_to ||
                                selectedMember?.manager ||
                                "â€”"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8 border-b border-gray-200 pb-8">
                    <h5 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                      Skills & Certifications
                    </h5>
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-3 inline-block">
                        Skills
                      </p>
                      <div className="flex flex-wrap gap-2 mb-5">
                        {(selectedMember?.skills || []).length === 0 ? (
                          <span className="text-sm text-gray-400 italic">
                            No skills listed
                          </span>
                        ) : (
                          (selectedMember?.skills || []).map((skill, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-800"
                            >
                              {skill}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-3 inline-block">
                        Certifications
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(selectedMember?.certifications || []).length === 0 ? (
                          <span className="text-sm text-gray-400 italic">
                            No certifications listed
                          </span>
                        ) : (
                          (selectedMember?.certifications || []).map(
                            (cert, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700"
                              >
                                <AcademicCapIcon className="w-3.5 h-3.5 mr-1.5" />
                                {cert}
                              </span>
                            ),
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedMember?.bio && (
                    <div className="mb-8 bg-gray-50 rounded-lg p-5 border border-gray-100">
                      <h5 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">
                        Bio
                      </h5>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {selectedMember.bio}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowMemberDetails(false)}
                      className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Performance Modal */}
        <AnimatePresence>
          {showPerformanceModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {modalMode === "create"
                        ? "Add Performance Record"
                        : "Edit Performance Record"}
                    </h3>
                    <button
                      onClick={() => setShowPerformanceModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employee ID
                      </label>
                      <input
                        type="text"
                        value={performanceForm.employee_id}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            employee_id: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employee Name
                      </label>
                      <input
                        type="text"
                        value={performanceForm.employee_name}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            employee_name: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        KPI Score
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.1"
                        value={performanceForm.kpi_score}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            kpi_score: parseFloat(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        KRI Score
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.1"
                        value={performanceForm.kri_score}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            kri_score: parseFloat(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Overall Rating
                      </label>
                      <select
                        value={performanceForm.overall_rating}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            overall_rating: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="satisfactory">Satisfactory</option>
                        <option value="needs_improvement">
                          Needs Improvement
                        </option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team Assignment
                      </label>
                      <input
                        type="text"
                        value={performanceForm.team_assignment}
                        onChange={(e) =>
                          setPerformanceForm({
                            ...performanceForm,
                            team_assignment: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowPerformanceModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePerformanceRecord}
                      className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {modalMode === "create" ? "Create" : "Update"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Relieved Employee Modal */}
        <AnimatePresence>
          {showRelievedModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {modalMode === "create"
                        ? "Add Relieved Employee"
                        : "Edit Relieved Employee"}
                    </h3>
                    <button
                      onClick={() => setShowRelievedModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employee Name
                      </label>
                      <input
                        type="text"
                        value={relievedForm.employee_name}
                        onChange={(e) =>
                          setRelievedForm({
                            ...relievedForm,
                            employee_name: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Working Day
                      </label>
                      <input
                        type="date"
                        value={relievedForm.lwd}
                        onChange={(e) =>
                          setRelievedForm({
                            ...relievedForm,
                            lwd: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for Leaving
                      </label>
                      <input
                        type="text"
                        value={relievedForm.reason_for_leaving}
                        onChange={(e) =>
                          setRelievedForm({
                            ...relievedForm,
                            reason_for_leaving: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Clearance Status
                      </label>
                      <select
                        value={relievedForm.clearance_status}
                        onChange={(e) =>
                          setRelievedForm({
                            ...relievedForm,
                            clearance_status: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>

                  {/* Clearance Checkboxes */}
                  {/* <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Clearance Checklist</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={relievedForm.assets_returned}
                          onChange={(e) => setRelievedForm({...relievedForm, assets_returned: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Assets Returned</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={relievedForm.access_revoked}
                          onChange={(e) => setRelievedForm({...relievedForm, access_revoked: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Access Revoked</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={relievedForm.exit_interview_completed}
                          onChange={(e) => setRelievedForm({...relievedForm, exit_interview_completed: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Exit Interview Completed</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={relievedForm.final_settlement}
                          onChange={(e) => setRelievedForm({...relievedForm, final_settlement: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Final Settlement</span>
                      </label>
                    </div>
                  </div> */}

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowRelievedModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateRelievedRecord}
                      className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700"
                    >
                      {modalMode === "create" ? "Create" : "Update"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      <UploadFileModal
        showUploadFile={performanceUploadFile}
        setShowUploadFile={setPerformanceUploadFile}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        handleUploadFile={handlePerformUploadFile}
        title="Upload Performance Document"
        subtitle="Upload documents for employee performance records"
        acceptedFileTypes=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        maxFileSize={2}
        fileTypeLabel={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
      />


    </>
  );
};
export default TeamCollaboration;

